import {
  createSignal,
  createEffect,
  onCleanup,
  onMount,
  Show,
  For,
  JSX,
} from 'solid-js';
import { debounce } from 'lodash';

//Props takes in a type which is for the list of items (api specific)
export interface ComboboxProps<T> {
  label?: string;
  value?: string | null;
  required?: boolean;
  disabled?: boolean;
  disableClear?: boolean;
  class?: string;
  error?: string | null;
  fetchData: (query: string) => Promise<T[]> | T[];
  getOptionLabel: (item: T) => string;
  renderItem?: (item: T, isActive: boolean) => JSX.Element;
  isOptionDisabled?: (item: T) => boolean;
  onSelect?: (item: T | null) => void;
}

//Likely refactor these const to props
const PAGE_JUMP = 10;
const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const LOADING_TEXT = 'Loading...';
const NO_RESULTS_TEXT = 'No options found';

export const Combobox = <T,>(props: ComboboxProps<T>) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [options, setOptions] = createSignal<T[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [inputValue, setInputValue] = createSignal(props.value ?? '');
  //activeIndex for styles, highlighting, etc.
  const [activeIndex, setActiveIndex] = createSignal(-1);
  const [searchError, setSearchError] = createSignal<string | null>(null);
  const [selectedItem, setSelectedItem] = createSignal<T | null>(null);

  let inputRef: HTMLInputElement | undefined;
  let dropdownRef: HTMLDivElement | undefined;
  let listboxRef: HTMLUListElement | undefined;

  // token to discard stale requests
  let requestToken = 0;
  let lastSyncedValue = props.value ?? '';

  // Syncs external `props.value` changes into the input and clears any selection it no longer matches.
  createEffect(() => {
    const next = props.value ?? '';

    if (next === lastSyncedValue) {
      return;
    }

    lastSyncedValue = next;
    setInputValue(next);

    const current = selectedItem();
    if (current !== null && props.getOptionLabel(current) !== next) {
      setSelectedItem(null);
    }

    // Parent changed the bound value, so discard stale options and cancel any in-flight request.
    debouncedSearch.cancel();
    requestToken++;
    setOptions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    // Bumping requestToken above orphans any in-flight request's finally block, so
    // reset the loading/error state here instead of leaving it stuck.
    setLoading(false);
    setSearchError(null);
  });

  // Keeps the highlighted option scrolled into view as the user navigates.
  createEffect(() => {
    const idx = activeIndex();
    if (!isOpen() || idx < 0 || !listboxRef) return;
    const el = listboxRef.children[idx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  });

  // Shared search that calls `fetchData` and guards against stale responses.
  const runSearch = async (query: string) => {
    if (query.trim().length < MIN_CHARS) {
      setOptions([]);
      setIsOpen(false);
      return;
    }
    //return if stale (user is actively typing).
    const token = ++requestToken;
    setLoading(true);
    setSearchError(null);
    setIsOpen(true);
    try {
      const results = await props.fetchData(query);
      if (token !== requestToken) return;
      setOptions(results);
      setActiveIndex(-1);
    } catch {
      if (token !== requestToken) return;
      setSearchError('Failed to fetch options');
      setOptions([]);
    } finally {
      if (token === requestToken) setLoading(false);
    }
  };

  const debouncedSearch = debounce(runSearch, DEBOUNCE_MS);

  // Handles user typing: updates state and triggers a search.
  const handleInputChange = (e: InputEvent) => {
    const newValue = (e.target as HTMLInputElement).value;
    setInputValue(newValue);
    setActiveIndex(-1);
    if (selectedItem() !== null) setSelectedItem(null);
    if (newValue.length === 0) {
      debouncedSearch.cancel();
      requestToken++;
      setOptions([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }
    void debouncedSearch(newValue);
  };

  // Commits an option selection and closes the dropdown.
  const handleOptionSelect = (item: T) => {
    setInputValue(props.getOptionLabel(item));
    setSelectedItem(() => item);
    setIsOpen(false);
    setActiveIndex(-1);
    props.onSelect?.(item);
  };

  // Clears the input and resets all internal state back to empty.
  const handleClear = () => {
    debouncedSearch.cancel();
    requestToken++;
    setInputValue('');
    setOptions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    setSelectedItem(null);
    setLoading(false);
    props.onSelect?.(null);
    inputRef?.focus();
  };

  // Opens existing options, or searches using the current input when prefilled.
  const openOrSearch = () => {
    if (Boolean(props.disabled)) return;
    if (options().length > 0) {
      setIsOpen(true);
      return;
    }
    const query = inputValue();
    if (query.trim().length < MIN_CHARS) return;
    debouncedSearch.cancel();
    void runSearch(query);
  };

  // Returns the index of the last option, or -1 when the list is empty.
  const lastIndex = () => options().length - 1;

  const isOptionDisabledAt = (index: number) => {
    const option = options()[index];
    return option !== undefined && (props.isOptionDisabled?.(option) ?? false);
  };

  const moveActiveIndex = (direction: 1 | -1) => {
    const count = options().length;
    if (count === 0) return;

    let idx = activeIndex();
    for (let i = 0; i < count; i++) {
      idx =
        direction === 1
          ? idx < lastIndex()
            ? idx + 1
            : 0
          : idx > 0
            ? idx - 1
            : lastIndex();
      if (!isOptionDisabledAt(idx)) {
        setActiveIndex(idx);
        return;
      }
    }
  };

  const findFirstEnabledIndex = () => {
    for (let i = 0; i < options().length; i++) {
      if (!isOptionDisabledAt(i)) return i;
    }
    return -1;
  };

  const findLastEnabledIndex = () => {
    for (let i = options().length - 1; i >= 0; i--) {
      if (!isOptionDisabledAt(i)) return i;
    }
    return -1;
  };

  const moveActiveIndexByPage = (direction: 1 | -1) => {
    const count = options().length;
    if (count === 0) return;

    const start = activeIndex() < 0 ? 0 : activeIndex();
    const target =
      direction === 1
        ? Math.min(lastIndex(), start + PAGE_JUMP)
        : Math.max(0, start - PAGE_JUMP);

    if (!isOptionDisabledAt(target)) {
      setActiveIndex(target);
      return;
    }

    moveActiveIndex(direction);
  };

  // Handles all keyboard navigation for the combobox per the ARIA pattern.
  // eslint-disable-next-line complexity
  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen()) {
          openOrSearch();
          return;
        }
        if (options().length === 0) return;
        moveActiveIndex(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen()) {
          openOrSearch();
          return;
        }
        if (options().length === 0) return;
        moveActiveIndex(-1);
        break;
      case 'Home':
        if (!isOpen() || options().length === 0) return;
        e.preventDefault();
        setActiveIndex(findFirstEnabledIndex());
        break;
      case 'End':
        if (!isOpen() || options().length === 0) return;
        e.preventDefault();
        setActiveIndex(findLastEnabledIndex());
        break;
      case 'PageDown':
        if (!isOpen() || options().length === 0) return;
        e.preventDefault();
        moveActiveIndexByPage(1);
        break;
      case 'PageUp':
        if (!isOpen() || options().length === 0) return;
        e.preventDefault();
        moveActiveIndexByPage(-1);
        break;
      case 'Enter':
        if (
          isOpen() &&
          activeIndex() >= 0 &&
          options()[activeIndex()] &&
          !isOptionDisabledAt(activeIndex())
        ) {
          e.preventDefault();
          handleOptionSelect(options()[activeIndex()]);
        }
        break;
      case 'Escape':
        if (isOpen()) {
          e.preventDefault();
          setIsOpen(false);
          setActiveIndex(-1);
        } else {
          inputRef?.blur();
        }
        break;
      case 'Tab':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  // Closes the dropdown when a mousedown occurs outside the input and listbox.
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef &&
      !dropdownRef.contains(event.target as Node) &&
      inputRef &&
      !inputRef.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    debouncedSearch.cancel();
  });

  // Resolves the input's resting border color based on error state.
  const borderColor = () =>
    Boolean(props.error) ? 'border-error-red' : 'border-input-border';
  // Resolves the input's focus-state border color based on error state.
  const focusBorderColor = () =>
    Boolean(props.error)
      ? 'focus:border-error-red'
      : 'focus:border-armstrong-primary';
  // Resolves the input's hover-state border color based on error state.
  const hoverBorderColor = () =>
    Boolean(props.error)
      ? 'hover:border-error-red'
      : 'hover:border-armstrong-primary';

  // True when the query is long enough to search but returned no results.
  const showNoResults = () =>
    !loading() &&
    !Boolean(searchError()) &&
    options().length === 0 &&
    inputValue().length >= MIN_CHARS;

  return (
    <div class="relative my-1">
      <div class="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder=""
          aria-invalid={Boolean(props.error) || undefined}
          aria-required={props.required || undefined}
          class={`peer w-full px-3 py-2 pr-8 border ${borderColor()} rounded-md shadow-xs focus:outline-hidden ${focusBorderColor()} ${hoverBorderColor()} placeholder:opacity-0 focus:placeholder:opacity-100 ${
            Boolean(props.disabled) ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${props.class ?? ''}`}
          value={inputValue()}
          onInput={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={openOrSearch}
          disabled={props.disabled}
          autocomplete="off"
        />
        <Show when={Boolean(props.label)}>
          <label
            class={`text-shadow-thick absolute bg-transparent left-2 px-1 text-sm font-medium transition-all duration-200 ease-out pointer-events-none -top-2.5
              peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-gray-500 peer-focus:-top-2.5 peer-focus:text-sm peer-disabled:text-gray-400
              ${Boolean(props.error) ? 'text-error-red peer-focus:text-error-red' : 'text-armstrong-primary peer-focus:text-armstrong-primary'}`}
          >
            {props.label}
            {props.required && <span class="text-error-red"> *</span>}
          </label>
        </Show>
        <Show
          when={
            Boolean(inputValue()) &&
            !Boolean(props.disabled) &&
            props.disableClear !== true
          }
        >
          <button
            type="button"
            onClick={handleClear}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-700 hover:text-black cursor-pointer focus:outline-hidden"
            aria-label="Clear"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4Z" />
            </svg>
          </button>
        </Show>
      </div>

      <Show when={Boolean(props.error)}>
        <div class="mt-1 text-sm text-error-red">{props.error}</div>
      </Show>

      <Show when={isOpen()}>
        <div
          ref={dropdownRef}
          class="absolute z-50 w-full mt-1 bg-white border border-input-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          <Show when={loading()}>
            <div class="px-3 py-2 text-sm text-gray-500 flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-armstrong-primary mr-2"></div>
              {LOADING_TEXT}
            </div>
          </Show>

          <Show when={Boolean(searchError())}>
            <div class="px-3 py-2 text-sm text-error-red" role="alert">
              {searchError()}
            </div>
          </Show>

          <Show when={showNoResults()}>
            <div class="px-3 py-2 text-sm text-gray-500">{NO_RESULTS_TEXT}</div>
          </Show>

          <ul ref={listboxRef} class="list-none m-0 p-0">
            <Show when={!loading() && !Boolean(searchError())}>
              <For each={options()}>
                {(option, index) => {
                  const isActive = () => index() === activeIndex();
                  const disabled = () =>
                    props.isOptionDisabled?.(option) ?? false;
                  return (
                    <li
                      class={`px-3 py-2 text-sm ${
                        disabled()
                          ? 'cursor-event-none opacity-50'
                          : 'cursor-pointer'
                      } ${isActive() && !disabled() ? 'bg-grayish' : ''}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (!disabled()) {
                          handleOptionSelect(option);
                        }
                      }}
                      onMouseEnter={() => {
                        if (!disabled()) {
                          setActiveIndex(index());
                        }
                      }}
                      aria-disabled={disabled() || undefined}
                    >
                      {props.renderItem
                        ? props.renderItem(option, isActive())
                        : props.getOptionLabel(option)}
                    </li>
                  );
                }}
              </For>
            </Show>
          </ul>
        </div>
      </Show>
    </div>
  );
};
